import React from 'react';
import InteractiveField from '../common/InteractiveField';
import MultilineField from '../common/MultilineField';
import { getHandoverActNumber, getReturnActNumber } from '../../utils/attachmentNumbers';

/**
 * Компонент для раздела "3. Права и обязанности Сторон".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 * @param {object} contractData - Все данные договора для вычисления номеров приложений.
 */
const RightsAndObligationsSection = ({ data, onChange, contractData }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    returnPeriodDays = 3,
    includeOtherTenantObligations = false,
    otherTenantObligations = '',
    tenantRights = { includeOtherRights: false, otherRights: '' },
    handoverOption = 'on_signing_day',
    handoverPeriodDays = 5,
    capitalRepairs = { enabled: true, includePeriod: false, period: '', includeWorkList: false, workList: '' },
    includeInspectionClause = true,
    inspectionPeriod = '1 раз в месяц',
  } = data;

  // Динамические счетчики
  let tenantObligationCounter = 5;
  let tenantRightsCounter = 1;

  // Универсальный обработчик для полей верхнего уровня
  const handleFieldChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  // Обработчики для вложенных объектов
  const handleCapitalRepairsChange = (field, value) => {
    onChange({ ...data, capitalRepairs: { ...capitalRepairs, [field]: value } });
  };
  const handleCapitalRepairsToggle = (field) => {
    onChange({ ...data, capitalRepairs: { ...capitalRepairs, [field]: !capitalRepairs[field] } });
  };
  const handleTenantRightsToggle = (field) => {
    onChange({ ...data, tenantRights: { ...tenantRights, [field]: !tenantRights[field] } });
  };
  const handleTenantRightsChange = (field, value) => {
    onChange({ ...data, tenantRights: { ...tenantRights, [field]: value } });
  };

  // Получаем номера приложений динамически
  const handoverActNumber = getHandoverActNumber();
  const returnActNumber = getReturnActNumber(contractData);

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        3. Права и обязанности Сторон
      </h2>

      {/* ======================================================================= */}
      {/* 3.1. НАНИМАТЕЛЬ ОБЯЗАН */}
      {/* ======================================================================= */}
      <p className="document-preamble"><b>3.1. Наниматель обязан:</b></p>
      
      <p className="document-preamble"><b>3.1.1.</b> В указанные сроки и в установленном порядке вносить плату за Помещение, а также за услуги, если они согласованы в настоящем Договоре (коммунальные услуги, иные услуги, а также взносы на капитальный ремонт).</p>
      <p className="document-preamble"><b>3.1.2.</b> Использовать Помещение только для проживания, обеспечивать сохранность жилого помещения и поддерживать его в надлежащем состоянии.</p>
      <p className="document-preamble"><b>3.1.3.</b> Не нарушать правила пользования жилыми помещениями, а также права и интересы соседей.</p>
      <p className="document-preamble"><b>3.1.4.</b> Обеспечивать сохранность и поддерживать в исправном состоянии Помещение и находящееся в нем оборудование, а также имущество. В случае возникновения неисправностей Помещения или его оборудования, при необходимости незамедлительно произвести действия, направленные на их устранение, а также сообщить о них Наймодателю и в соответствующую управляющую организацию.</p>
      <p className="document-preamble"><b>3.1.5.</b> Поддерживать чистое состояние и порядок в Помещении.</p>

      {/* Пункт 3.1.6 */}
      <p className="document-preamble">
        <b>3.1.{++tenantObligationCounter}.</b> В случае прекращения настоящего Договора, в том числе при его расторжении, освободить Помещение в <InteractiveField value={returnPeriodDays} onChange={(v) => handleFieldChange('returnPeriodDays', v)} placeholder="3" type="number" isRequired={true} />-дневный срок и возвратить Наймодателю по Акту возврата (Приложение № {returnActNumber}) в исправном состоянии Помещение, ранее переданное имущество и оборудование, а также оплатить задолженность за Помещение и услуги, если они согласованы в настоящем Договоре (коммунальные услуги, иные услуги, а также взносы на капитальный ремонт).
      </p>

      {/* Опциональный пункт 3.1.8 */}
      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeOtherTenantObligations ? 'selected' : ''}`}
            onClick={() => handleFieldChange('includeOtherTenantObligations', !includeOtherTenantObligations)}
          >
            {includeOtherTenantObligations ? '✓ Иные обязанности Нанимателя' : '+ Добавить иные обязанности'}
          </button>
        </div>
        {includeOtherTenantObligations && (
          <p className="document-preamble">
            <b>3.1.{++tenantObligationCounter}.</b> <MultilineField value={otherTenantObligations} onChange={(v) => handleFieldChange('otherTenantObligations', v)} placeholder="Производить уборку в общем коридоре совместно с соседями" isRequired={true} minRows={1} maxRows={5} />
          </p>
        )}
      </div>

      {/* Пункт 3.1.9 */}
      <p className="document-preamble">
        <b>3.1.{++tenantObligationCounter}.</b> Соблюдать и другие обязанности, предусмотренные законодательством Российской Федерации.
      </p>

      {/* ======================================================================= */}
      {/* 3.2. НАНИМАТЕЛЬ ВПРАВЕ */}
      {/* ======================================================================= */}
      <p className="document-preamble"><b>3.2. Наниматель вправе:</b></p>

      <p className="document-preamble"><b>3.2.{tenantRightsCounter}.</b> Использовать общее имущество многоквартирного дома.</p>

      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${tenantRights.includeOtherRights ? 'selected' : ''}`}
            onClick={() => handleTenantRightsToggle('includeOtherRights')}
          >
            {tenantRights.includeOtherRights ? '✓ Иные права Нанимателя' : '+ Добавить иные права'}
          </button>
        </div>
        {tenantRights.includeOtherRights && (
          <p className="document-preamble">
            <b>3.2.{++tenantRightsCounter}.</b> <MultilineField value={tenantRights.otherRights} onChange={(v) => handleTenantRightsChange('otherRights', v)} placeholder="Пользоваться парковочным местом" isRequired={true} minRows={1} maxRows={5} />
          </p>
        )}
      </div>

      <p className="document-preamble">
        <b>3.2.{++tenantRightsCounter}.</b> Осуществлять и другие права, предусмотренные законодательством Российской Федерации.
      </p>

      {/* ======================================================================= */}
      {/* 3.3. НАЙМОДАТЕЛЬ ОБЯЗАН */}
      {/* ======================================================================= */}
      <p className="document-preamble"><b>3.3. Наймодатель обязан:</b></p>
      
      {/* Конфигурируемый пункт 3.3.1 */}
      <div className="configurable-section dynamic-content-block active-purple">
        {handoverOption === 'on_signing_day' && (
          <p className="document-preamble"><b>3.3.1.</b> Передать Нанимателю Помещение в состоянии, пригодном для проживания, в соответствии с условиями настоящего Договора в день заключения Договора. Подписание Акта приема-передачи жилого Помещения (Приложение № {handoverActNumber}) свидетельствует о передаче в наем Нанимателю Помещения и имущества, находящегося в нем.</p>
        )}
        {handoverOption === 'within_period_signing' && (
          <p className="document-preamble"><b>3.3.1.</b> Передать Нанимателю Помещение в состоянии, пригодном для проживания, в соответствии с условиями настоящего Договора в <InteractiveField value={handoverPeriodDays} onChange={(v) => handleFieldChange('handoverPeriodDays', v)} placeholder="5" type="number" isRequired={true} />-дневный срок с момента заключения Договора. Подписание Акта приема-передачи жилого Помещения (Приложение № {handoverActNumber}) свидетельствует о передаче в наем Нанимателю Помещения и имущества, находящегося в нем.</p>
        )}
        {handoverOption === 'within_period_prepayment' && (
          <p className="document-preamble"><b>3.3.1.</b> Передать Нанимателю Помещение в состоянии, пригодном для проживания, в соответствии с условиями настоящего Договора в <InteractiveField value={handoverPeriodDays} onChange={(v) => handleFieldChange('handoverPeriodDays', v)} placeholder="3" type="number" isRequired={true} />-дневный срок с момента внесения предоплаты. Подписание Акта приема-передачи жилого Помещения (Приложение № {handoverActNumber}) свидетельствует о передаче в наем Нанимателю Помещения и имущества, находящегося в нем.</p>
        )}
        
        <div className="option-pills-group" style={{ marginTop: '16px' }}>
          <button className={`option-pill ${handoverOption === 'on_signing_day' ? 'selected' : ''}`} onClick={() => handleFieldChange('handoverOption', 'on_signing_day')}>В день заключения договора</button>
          <button className={`option-pill ${handoverOption === 'within_period_signing' ? 'selected' : ''}`} onClick={() => handleFieldChange('handoverOption', 'within_period_signing')}>В течение срока с момента заключения договора</button>
          <button className={`option-pill ${handoverOption === 'within_period_prepayment' ? 'selected' : ''}`} onClick={() => handleFieldChange('handoverOption', 'within_period_prepayment')}>В течение срока с момента внесения предоплаты</button>
        </div>
      </div>

      <p className="document-preamble"><b>3.3.2.</b> Обеспечивать Нанимателю предоставление надлежащего качества коммунальных и иных услуг, согласованных в настоящем Договоре.</p>
      <p className="document-preamble"><b>3.3.3.</b> Принять в согласованные сторонами сроки Помещение у Нанимателя в случае прекращения настоящего Договора, в том числе по причине расторжения. Подписание Акта возврата Помещения (Приложение № {returnActNumber}) свидетельствует о возврате Нанимателем используемого Помещения.</p>

      {/* Конфигурируемый пункт 3.3.4 */}
      <div className="configurable-section dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button className={`option-pill-toggle ${capitalRepairs.includePeriod ? 'selected' : ''}`} onClick={() => handleCapitalRepairsToggle('includePeriod')}>
            {capitalRepairs.includePeriod ? '✓ Указаны сроки' : '+ Указать сроки'}
          </button>
          <button className={`option-pill-toggle ${capitalRepairs.includeWorkList ? 'selected' : ''}`} onClick={() => handleCapitalRepairsToggle('includeWorkList')}>
            {capitalRepairs.includeWorkList ? '✓ Указан перечень работ' : '+ Указать перечень работ'}
          </button>
        </div>
        <p className="document-preamble"><b>3.3.4.</b> Осуществлять капитальный ремонт Помещения за свой счет.
          {capitalRepairs.includePeriod && (
            <> Капитальный ремонт должен производиться каждые <InteractiveField value={capitalRepairs.period} onChange={(v) => handleCapitalRepairsChange('period', v)} placeholder="5 лет" isRequired={true} />.</>
          )}
          {capitalRepairs.includeWorkList && (
            <> В капитальный ремонт входят следующие работы: <MultilineField value={capitalRepairs.workList} onChange={(v) => handleCapitalRepairsChange('workList', v)} placeholder="при необходимости замена изношенных покрытий стен и потолков" isRequired={true} minRows={1} maxRows={5} /></>
          )}
        </p>
      </div>

      <p className="document-preamble"><b>3.3.5.</b> Соблюдать и другие обязанности, предусмотренные законодательством Российской Федерации.</p>

      {/* ======================================================================= */}
      {/* 3.4. НАЙМОДАТЕЛЬ ВПРАВЕ */}
      {/* ======================================================================= */}
      <p className="document-preamble"><b>3.4. Наймодатель вправе:</b></p>
      
      <p className="document-preamble"><b>3.4.1.</b> Требовать в указанные сроки плату за Помещение, а также за услуги, если они согласованы в настоящем Договоре (коммунальные услуги, иные услуги, а также взносы на капитальный ремонт).</p>
      
      <div className="optional-field-block dynamic-content-block active-green">
        <div className="option-pills-group" style={{ marginBottom: '16px' }}>
          <button
            className={`option-pill-toggle ${includeInspectionClause ? 'selected' : ''}`}
            onClick={() => handleFieldChange('includeInspectionClause', !includeInspectionClause)}
          >
            {includeInspectionClause ? '✓ Включить пункт о посещении' : '+ Добавить пункт о посещении'}
          </button>
        </div>
        {includeInspectionClause && (
          <p className="document-preamble">
            <b>3.4.2.</b> Посещать Помещение для проверки его надлежащего использования в согласованное с Нанимателем время с периодичностью <InteractiveField value={inspectionPeriod} onChange={(v) => handleFieldChange('inspectionPeriod', v)} placeholder="1 раз в месяц" isRequired={true} />.
          </p>
        )}
      </div>

    </div>
  );
};

export default RightsAndObligationsSection;